import { useState } from "react";
import { useDashboard } from "../hooks/useDashboard";
import { DashboardHeader } from "../components/dashboard/DashboardHeader";
import { AlertMessage } from "../components/dashboard/AlertMessage";
import { BotControl } from "../components/dashboard/BotControl";
import { AmountManagement } from "../components/dashboard/AmountManagement";
import { MartingaleControl } from "../components/dashboard/MartingaleControl";
import { TargetControl } from "../components/dashboard/TargetControl";
import { TrendTrackerControl } from "../components/dashboard/TrendTrackerControl";
import { CooldownControl } from "../components/dashboard/CooldownControl";
import { StatsCard } from "../components/dashboard/StatsCard";
import { DirectionHistory } from "../components/dashboard/DirectionHistory";
import { OngoingTrade } from "../components/dashboard/OngoingTrade";
import { StatsSummary } from "../components/dashboard/StatsSummary";

export const Dashboard = () => {
    const [showConfig, setShowConfig] = useState(false);
    const [showMonitoring, setShowMonitoring] = useState(false);

    const {
        botStatus,
        loading,
        error,
        success,
        statusLoading,
        currentAmount,
        currentMartingale,
        currentTarget,
        currentTrendTracker,
        dailyStats,
        cooldownStatus,
        cooldownSettings,
        userEmail,
        userRole,
        handleStart,
        handleStop,
        handleRestart,
        handleSetAmount,
        handleSetMartingale,
        handleSetTarget,
        handleSetTrendTracker,
        handleSetCooldown,
        handleLogout,
        getStatusColor
    } = useDashboard();

    return (
        <div className="min-h-screen bg-slate-900 p-2">
            <div className="max-w-7xl mx-auto">
                <DashboardHeader 
                    userEmail={userEmail} 
                    userRole={userRole} 
                    onLogout={handleLogout} 
                />

                <AlertMessage type="error" message={error} />
                <AlertMessage type="success" message={success} />

                {/* Stats Overview */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-2 mb-2">
                    <StatsCard 
                        title="Status" 
                        value={botStatus}
                        color="blue"
                        icon="🤖"
                    />
                    <StatsCard 
                        title="Current Amount" 
                        value={currentAmount !== null ? `$${currentAmount}` : '--'}
                        color="blue"
                        icon="💵"
                    />
                    <StatsCard 
                        title="Daily Target" 
                        value={dailyStats.target !== null ? `$${dailyStats.target}` : '--'}
                        color="purple"
                        icon="🎯"
                        trend={dailyStats.targetReached ? '✅ Reached' : '⏳ In Progress'}
                    />
                    <StatsCard 
                        title="Today's Profit" 
                        value={dailyStats.profit !== null ? `$${dailyStats.profit}` : '--'}
                        color="green"
                        icon="💰"
                    />
                    <StatsCard 
                        title="Remaining" 
                        value={dailyStats.remaining !== null ? `$${dailyStats.remaining}` : '--'}
                        color="amber"
                        icon="📊"
                    />
                    <StatsCard 
                        title="Martingale" 
                        value={currentMartingale !== null ? currentMartingale : '--'}
                        color="pink"
                        icon="🔄"
                    />
                    <StatsCard 
                        title="Cooldown" 
                        value={cooldownStatus?.inCooldown ? `${cooldownStatus.remainingMinutes}m` : 'Ready'}
                        color="cyan"
                        icon="⏱️"
                        trend={cooldownStatus?.inCooldown ? '🔒 Active' : '✅ Clear'}
                    />
                </div>

                {/* Main Controls Section */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 mb-2">
                    {/* Bot Control - Takes 2 columns */}
                    <div className="lg:col-span-2">
                        <BotControl
                            botStatus={botStatus}
                            statusLoading={statusLoading}
                            loading={loading}
                            onStart={handleStart}
                            onStop={handleStop}
                            onRestart={handleRestart}
                            getStatusColor={getStatusColor}
                        />
                    </div>

                    {/* Amount Management - Takes 1 column */}
                    <div>
                        <AmountManagement
                            currentAmount={currentAmount}
                            loading={loading}
                            onSetAmount={handleSetAmount}
                        />
                    </div>
                </div>

                {/* Configuration Section - Collapsible */}
                <div className="bg-slate-800/50 p-2 rounded-lg border border-slate-700/50 mb-2">
                    <div 
                        className="flex items-center justify-between cursor-pointer hover:bg-slate-700/30 p-1 rounded transition-colors"
                        onClick={() => {
                            setShowConfig(!showConfig);
                            if (!showConfig) setShowMonitoring(false);
                        }}
                    >
                        <h3 className="text-xs font-semibold text-white flex items-center gap-1">
                            <span>⚙️</span> Trading Configuration
                        </h3>
                        <button className="text-slate-400 hover:text-white transition-colors text-sm">
                            {showConfig ? '▼ Hide' : '▶ Show'}
                        </button>
                    </div>
                    
                    {showConfig && (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mt-2">
                                <MartingaleControl
                                    currentMartingale={currentMartingale}
                                    loading={loading}
                                    onSetMartingale={handleSetMartingale}
                                />

                                <TargetControl
                                    currentTarget={currentTarget}
                                    loading={loading}
                                    onSetTarget={handleSetTarget}
                                />

                                <TrendTrackerControl
                                    currentTrendTracker={currentTrendTracker}
                                    loading={loading}
                                    onSetTrendTracker={handleSetTrendTracker}
                                />

                                <CooldownControl
                                    cooldownSettings={cooldownSettings}
                                    cooldownStatus={cooldownStatus}
                                    loading={loading}
                                    onSetCooldown={handleSetCooldown}
                                />
                            </div>
                            <div className="mt-2">
                                <StatsSummary
                                    botStatus={botStatus}
                                    dailyStats={dailyStats}
                                    currentMartingale={currentMartingale}
                                    currentAmount={currentAmount}
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* Trade Monitoring Section - Collapsible */}
                <div className="bg-slate-800/50 p-2 rounded-lg border border-slate-700/50 mb-2">
                    <div 
                        className="flex items-center justify-between cursor-pointer hover:bg-slate-700/30 p-1 rounded transition-colors"
                        onClick={() => {
                            setShowMonitoring(!showMonitoring);
                            if (!showMonitoring) setShowConfig(false);
                        }}
                    >
                        <h3 className="text-xs font-semibold text-white flex items-center gap-1">
                            <span>📊</span> Trade Monitoring
                        </h3>
                        <button className="text-slate-400 hover:text-white transition-colors text-sm">
                            {showMonitoring ? '▼ Hide' : '▶ Show'}
                        </button>
                    </div>
                    
                    {showMonitoring && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 mt-2">
                            <div className="lg:col-span-1">
                                <OngoingTrade />
                            </div>
                            {/*<div className="lg:col-span-2">*/}
                            {/*    <DirectionHistory />*/}
                            {/*</div>*/}
                        </div>
                    )}
                </div>

                {/* Info Footer */}
                <div className="bg-slate-800/30 p-2 rounded-lg border border-slate-700/30">
                    <div className="flex items-center gap-2">
                        <span className="text-blue-400">ℹ️</span>
                        <p className="text-slate-400 text-xs">
                            Updates every 30s • Configure parameters before starting
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
